import { useAppController } from 'customHooks/useAppController'
import { newProjectActions } from 'features/BillOfMaterials/store/projectReducer'
import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { PdfViewerModel } from 'services/APIs/InternalAPI/internal-api.contracts'
import { isRequestCancelled } from 'services/APIs/BaseAPI'
import { useAppDispatch } from 'store/configureStore'
import { ProjectState } from 'store/Project/ProjectTypes'
import { useShowException } from 'utils/useShowException'
import { PdfViewerController } from '../AttachmentsController'

export const usePdfViewModel = (
  bomItemId: string,
  filterByFileName?: string,
  open: boolean = false,
) => {
  const [pdfViewModel, setPdfViewModel] = useState<PdfViewerModel>(null)
  const [pdfViewModelError, setPdfViewModelError] = useState(false)

  const { controller, loading } = useAppController(
    () => new PdfViewerController(),
  )

  const dispatch = useAppDispatch()

  const ShowException = useShowException('viewer')

  const getViewModel = useCallback(async () => {
    if (!bomItemId || !open) {
      return null
    }

    const pdfViewModel = await controller.GetPDFViewer(bomItemId)

    if (!pdfViewModel) {
      return null
    }

    if (filterByFileName) {
      pdfViewModel.pages = pdfViewModel.pages.filter((page) =>
        page.originalFileName.includes(filterByFileName),
      )
    }

    dispatch(
      newProjectActions.setProjectStateProperties({
        currentCheckList: {
          shallUpdateCheckList: true,
        },
      } as ProjectState),
    )

    return pdfViewModel
  }, [bomItemId, controller, dispatch, filterByFileName, open])

  const debouncedGetViewModel = useMemo(
    () => debounce(getViewModel, 500, { leading: true, trailing: true }),
    [getViewModel],
  )

  const getPDFViewModel = useCallback(async () => {
    try {
      setPdfViewModelError(false)
      const pdfViewModel = await debouncedGetViewModel()
      if (pdfViewModel) {
        setPdfViewModel(pdfViewModel)
      }
      return pdfViewModel
    } catch (err) {
      if (!isRequestCancelled(err)) {
        setPdfViewModelError(true)
        ShowException(err)
      }
      return null
    }
  }, [ShowException, debouncedGetViewModel])

  useEffect(() => {
    setPdfViewModel(null)
    setPdfViewModelError(false)
    getPDFViewModel()

    return () => {
      debouncedGetViewModel.cancel()
      controller.CancelRequests()
    }
  }, [controller, debouncedGetViewModel, getPDFViewModel])

  const reloadPdfViewerModel = useCallback(async () => {
    return getPDFViewModel()
  }, [getPDFViewModel])

  const handleDeletePdfPage = useCallback(
    async (pageId: string) => {
      try {
        const resp = await controller.DeletePdfPage(bomItemId, pageId)

        if (resp) {
          setPdfViewModel(resp)
        }
      } catch (err) {
        ShowException(err)
      }
    },
    [ShowException, bomItemId, controller],
  )

  return {
    pdfViewModel,
    pdfViewModelError,
    setPdfViewModel,
    loading,
    reloadPdfViewerModel,
    debouncedGetViewModel,
    handleDeletePdfPage,
  }
}
